// Custo de Mercadoria — comparativo mensal + previsão de contas a pagar
// das notas de entrada do Tiny (parcelas). Custos mensais editáveis inline.

import { Fragment, useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import AppLayout from '../../components/layout/AppLayout'
import KpiCard from '../../components/dashboard/KpiCard'
import Alert from '../../components/ui/Alert'
import CostOfGoodsChart from '../../components/cost-of-goods/CostOfGoodsChart'
import ContasPagarForecastChart from '../../components/cost-of-goods/ContasPagarForecastChart'
import {
  useCostOfGoods,
  computeRow,
  monthLabel,
  payablesTotals,
  todayLocalISO,
  type CostOfGoodsInput,
  type PurchaseInvoiceRow,
  type BoletoRow,
} from '../../hooks/useCostOfGoods'
import { useToast } from '../../hooks/useToast'
import type { CostOfGoodsEntry } from '../../types'

function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

const STATUS_BADGE = {
  ok:      { cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40', label: 'OK' },
  warning: { cls: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/40',    label: 'Atenção' },
  danger:  { cls: 'bg-red-900/40 text-red-300 border-red-700/40',             label: 'Acima' },
  empty:   { cls: 'bg-sunken text-ink-muted border-line',                     label: '—' },
} as const

type EditableField = 'cost_matriz' | 'cost_filial' | 'cost_filial_return' | 'revenue_gross' | 'target_pct'

interface DraftRow extends Record<EditableField, number> {
  notes: string
}

function entryToDraft(e: CostOfGoodsEntry): DraftRow {
  return {
    cost_matriz: Number(e.cost_matriz) || 0,
    cost_filial: Number(e.cost_filial) || 0,
    cost_filial_return: Number(e.cost_filial_return) || 0,
    revenue_gross: Number(e.revenue_gross) || 0,
    target_pct: Number(e.target_pct) || 55,
    notes: e.notes ?? '',
  }
}

function brlExact(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
}

// Sugestão automática (das notas do Tiny) sob o input manual. Mostra "usar"
// quando o valor auto difere do manual atual.
function AutoHint({ auto, current, onUse }: { auto: number | null | undefined; current: number; onUse: (v: number) => void }) {
  if (auto == null) return null
  const diff = Math.abs(auto - current) >= 0.5
  return (
    <div className="mt-1 text-right text-[11px] text-ink-faint tabular-nums whitespace-nowrap">
      <span title="Sugestão calculada das notas de entrada do Tiny">auto: {brlExact(auto)}</span>
      {diff && (
        <button
          onClick={() => onUse(Number(auto.toFixed(2)))}
          className="ml-1.5 px-1.5 py-0.5 rounded bg-sky-900/40 hover:bg-sky-900/60 border border-sky-700/40 text-sky-300"
          title="Preencher o campo com a sugestão automática"
        >
          usar
        </button>
      )}
    </div>
  )
}

// Drill-down: notas de entrada (com parcelas) do mês.
function InvoicesPanel({ data }: { data: PurchaseInvoiceRow[] | 'loading' | undefined }) {
  if (data === 'loading' || data === undefined) {
    return <div className="text-xs text-ink-faint py-2">Carregando notas…</div>
  }
  if (data.length === 0) {
    return <div className="text-xs text-ink-faint py-2">Nenhuma nota de entrada sincronizada para este mês. Use "Sincronizar notas do Tiny".</div>
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs text-ink-muted font-medium">{data.length} nota(s) de entrada</div>
      <div className="grid gap-1.5">
        {data.map(inv => (
          <div key={inv.id} className="bg-surface border border-line rounded-lg px-3 py-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
              <div className="text-ink">
                <span className="font-mono text-ink-muted">NF {inv.numero ?? '—'}/{inv.serie ?? '—'}</span>
                <span className="mx-2 text-ink-faint">·</span>
                <span>{inv.emitente_nome ?? '—'}</span>
                {inv.cfop && <span className="ml-2 px-1.5 py-0.5 rounded bg-sunken text-ink-faint">CFOP {inv.cfop}</span>}
              </div>
              <div className="text-ink font-medium tabular-nums">{brlExact(inv.valor_total)}</div>
            </div>
            {inv.installments.length > 0 && (
              <table className="w-full mt-2 text-[11px] text-ink-muted">
                <thead>
                  <tr className="text-ink-faint">
                    <th className="text-left font-normal pr-3">Parcela</th>
                    <th className="text-left font-normal pr-3">Vencimento</th>
                    <th className="text-right font-normal pr-3">Valor</th>
                    <th className="text-left font-normal">Meio de pagamento</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.installments.map((p, i) => (
                    <tr key={i}>
                      <td className="pr-3">{p.numero ?? i + 1}</td>
                      <td className="pr-3 font-mono">{p.due_date ?? '—'}</td>
                      <td className="pr-3 text-right tabular-nums">{brlExact(p.valor)}</td>
                      <td>{meioLabel(p.id_meio_pagamento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Meio de pagamento da NFe: de-para dos códigos do Tiny (fallback = código cru).
const MEIO_PAGAMENTO: Record<string, string> = {
  '0': 'Outros', '1': 'Dinheiro', '2': 'Cheque', '3': 'Cartão de Crédito',
  '4': 'Cartão de Débito', '5': 'Crédito Loja', '10': 'Vale Alimentação',
  '11': 'Vale Refeição', '12': 'Vale Presente', '13': 'Vale Combustível',
  '15': 'Boleto', '16': 'Depósito', '17': 'PIX', '18': 'Transferência',
  '19': 'Carteira Digital', '90': 'Sem pagamento', '99': 'Outros',
}
const meioLabel = (id: string | null) => (id == null ? '—' : (MEIO_PAGAMENTO[id] ?? `#${id}`))

// dd/mm/aaaa sem passar por Date (evita fuso mudar o dia).
function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso
}
// Lista de pagamentos das notas de entrada da empresa ativa (parcelas do
// bloco Pagamento do Tiny), ordenada por vencimento. Cada linha:
// fornecedor · nota · vencimento · valor.
function PagamentosSection({ data, open, onToggle }: {
  data: BoletoRow[] | 'loading' | null
  open: boolean
  onToggle: () => void
}) {
  const list = Array.isArray(data) ? data : []
  const total = list.reduce((s, b) => s + b.valor, 0)
  const isArr = Array.isArray(data)
  return (
    <div className="bg-surface border border-line rounded-xl overflow-hidden flex flex-col">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-sunken transition-colors shrink-0 text-left"
        title="Pagamentos (parcelas) de todas as notas de entrada sincronizadas do Tiny"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`transition-transform text-ink-faint shrink-0 ${open ? 'rotate-90' : ''}`}>▸</span>
          <span className="text-sm font-semibold text-ink truncate">💰 Pagamentos das Notas de Entrada</span>
        </div>
        {isArr && (
          <div className="flex flex-col items-end shrink-0 leading-tight">
            <span className="text-xs text-ink font-semibold tabular-nums">{brlExact(total)}</span>
            <span className="text-[11px] text-ink-faint">{list.length} pagamentos</span>
          </div>
        )}
      </button>
      {open && (
        <div className="border-t border-line">
          {data === 'loading' && <div className="text-xs text-ink-faint px-4 py-3">Carregando pagamentos…</div>}
          {isArr && list.length === 0 && (
            <div className="text-xs text-ink-faint px-4 py-3">
              Nenhum pagamento sincronizado. Use "↻ Sincronizar notas do Tiny".
            </div>
          )}
          {isArr && list.length > 0 && (
            <div className="overflow-y-auto max-h-[70vh]">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col />
                  <col className="w-[64px]" />
                  <col className="w-[92px]" />
                  <col className="w-[104px]" />
                </colgroup>
                <thead className="text-xs text-ink-faint uppercase sticky top-0 bg-sunken z-10">
                  <tr className="border-b border-line">
                    <th className="text-left font-semibold px-3 py-2">Fornecedor</th>
                    <th className="text-left font-semibold px-1 py-2">Nota</th>
                    <th className="text-left font-semibold px-1 py-2">Venc.</th>
                    <th className="text-right font-semibold px-3 py-2">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(b => (
                    <tr key={b.id} className="border-b border-line/60 hover:bg-sunken">
                      <td className="px-3 py-1.5 text-ink">
                        <span className="block truncate" title={b.emitente_nome ?? undefined}>
                          {b.emitente_nome ?? '—'}
                        </span>
                      </td>
                      <td className="px-1 py-1.5 font-mono text-xs text-ink-muted truncate" title={`${b.nf_numero ?? ''}${b.nf_serie ? '/' + b.nf_serie : ''}`}>
                        {b.nf_numero ?? '—'}{b.nf_serie ? `/${b.nf_serie}` : ''}
                      </td>
                      <td className="px-1 py-1.5 font-mono text-xs text-ink-muted whitespace-nowrap">{fmtDate(b.due_date)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-ink font-medium whitespace-nowrap">{brlExact(b.valor)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-surface">
                  <tr className="border-t-2 border-line">
                    <td colSpan={2} className="px-3 py-2 text-xs text-ink-muted">Total geral</td>
                    <td colSpan={2} className="px-3 py-2 text-right tabular-nums text-ink font-semibold whitespace-nowrap">{brlExact(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function CostOfGoodsPage() {
  const { entries, loading, error, updateById, deleteEntry, addNextMonth, syncNotas, fetchMonthInvoices, fetchAllBoletos } = useCostOfGoods()
  const { notify } = useToast()
  const [drafts, setDrafts] = useState<Record<string, DraftRow>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [invoicesByEntry, setInvoicesByEntry] = useState<Record<string, PurchaseInvoiceRow[] | 'loading'>>({})
  const [boletos, setBoletos] = useState<BoletoRow[] | 'loading' | null>('loading')
  const [boletosOpen, setBoletosOpen] = useState(true)

  // Carrega os boletos ao montar — alimentam a previsão, o KPI e a tabela.
  useEffect(() => {
    let cancelled = false
    fetchAllBoletos().then(rows => { if (!cancelled) setBoletos(rows) })
    return () => { cancelled = true }
  }, [fetchAllBoletos])

  const boletosArr = Array.isArray(boletos) ? boletos : []
  const payTotals = useMemo(() => payablesTotals(boletosArr, todayLocalISO()), [boletosArr])

  function toggleBoletos() {
    setBoletosOpen(o => !o)
  }

  async function handleSyncNotas() {
    setSyncing(true)
    const err = await syncNotas()
    setSyncing(false)
    if (err) notify('error', `Erro ao sincronizar: ${err}`)
    else {
      notify('success', 'Notas do Tiny sincronizadas. Sugestões automáticas atualizadas.')
      setInvoicesByEntry({}) // invalida drill-down em cache
      // recarrega os boletos (alimentam a previsão + KPI + tabela)
      setBoletos('loading')
      setBoletos(await fetchAllBoletos())
    }
  }

  async function toggleExpand(e: CostOfGoodsEntry) {
    if (expandedId === e.id) { setExpandedId(null); return }
    setExpandedId(e.id)
    if (!invoicesByEntry[e.id]) {
      setInvoicesByEntry(prev => ({ ...prev, [e.id]: 'loading' }))
      const rows = await fetchMonthInvoices(e.period_year, e.period_month)
      setInvoicesByEntry(prev => ({ ...prev, [e.id]: rows }))
    }
  }

  // Aplica uma sugestão automática ao draft do campo correspondente.
  function applyAuto(e: CostOfGoodsEntry, field: EditableField, value: number) {
    setDrafts(prev => ({ ...prev, [e.id]: { ...getDraftFor(e, prev), [field]: value } }))
  }
  function getDraftFor(e: CostOfGoodsEntry, prev: Record<string, DraftRow>): DraftRow {
    return prev[e.id] ?? entryToDraft(e)
  }

  // KPIs do mês corrente (ou último mês disponível)
  const currentEntry = useMemo(() => {
    if (entries.length === 0) return null
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1
    return entries.find(e => e.period_year === year && e.period_month === month)
           ?? entries[entries.length - 1]
  }, [entries])

  const currentComputed = currentEntry ? computeRow(currentEntry) : null

  const periodRangeLabel = useMemo(() => {
    if (entries.length === 0) return ''
    const first = entries[0]
    const last = entries[entries.length - 1]
    return `${monthLabel(first.period_year, first.period_month)} → ${monthLabel(last.period_year, last.period_month)}`
  }, [entries])

  function getDraft(e: CostOfGoodsEntry): DraftRow {
    return drafts[e.id] ?? entryToDraft(e)
  }

  function isDirty(e: CostOfGoodsEntry): boolean {
    const d = drafts[e.id]
    if (!d) return false
    const orig = entryToDraft(e)
    return (
      d.cost_matriz !== orig.cost_matriz ||
      d.cost_filial !== orig.cost_filial ||
      d.cost_filial_return !== orig.cost_filial_return ||
      d.revenue_gross !== orig.revenue_gross ||
      d.target_pct !== orig.target_pct ||
      d.notes !== orig.notes
    )
  }

  function setField(e: CostOfGoodsEntry, field: EditableField, value: string) {
    const num = Number(value.replace(/[^\d.,-]/g, '').replace(',', '.'))
    setDrafts(prev => ({
      ...prev,
      [e.id]: { ...getDraft(e), [field]: isNaN(num) ? 0 : num },
    }))
  }

  async function handleSave(e: CostOfGoodsEntry) {
    const d = getDraft(e)
    setSavingId(e.id)
    const patch: Partial<CostOfGoodsInput> = {
      cost_matriz: d.cost_matriz,
      cost_filial: d.cost_filial,
      cost_filial_return: d.cost_filial_return,
      revenue_gross: d.revenue_gross,
      target_pct: d.target_pct,
      notes: d.notes,
    }
    const err = await updateById(e.id, patch)
    setSavingId(null)
    if (err) {
      notify('error', `Erro ao salvar: ${err}`)
    } else {
      notify('success', `${monthLabel(e.period_year, e.period_month)} salvo.`)
      setDrafts(prev => {
        const next = { ...prev }
        delete next[e.id]
        return next
      })
    }
  }

  async function handleDelete(e: CostOfGoodsEntry) {
    const label = monthLabel(e.period_year, e.period_month)
    if (!confirm(`Excluir o registro de ${label}? Esta ação não pode ser desfeita.`)) return
    setDeletingId(e.id)
    const err = await deleteEntry(e.id)
    setDeletingId(null)
    if (err) notify('error', `Erro: ${err}`)
    else notify('success', `${label} excluído.`)
  }

  async function handleAddNext() {
    const err = await addNextMonth()
    if (err) notify('error', `Erro: ${err}`)
    else notify('success', 'Próximo mês adicionado.')
  }

  function handleExport() {
    if (entries.length === 0) {
      notify('error', 'Sem dados para exportar.')
      return
    }
    const data = entries.map(e => {
      const c = computeRow(e)
      return {
        Período: monthLabel(e.period_year, e.period_month),
        Ano: e.period_year,
        Mês: e.period_month,
        'Custo Matriz': Number(e.cost_matriz),
        'Custo Filial': Number(e.cost_filial),
        'Retorno Filial': Number(e.cost_filial_return),
        'Custo Real': c.costReal,
        Faturamento: Number(e.revenue_gross),
        'Custo %': c.costPct != null ? Number(c.costPct.toFixed(2)) : '',
        'Meta %': Number(e.target_pct),
        'Meta R$': c.targetValue,
        Status: STATUS_BADGE[c.status].label,
        Observações: e.notes ?? '',
      }
    })
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Custo de Mercadoria')
    XLSX.writeFile(wb, `custo_mercadoria_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  return (
    <AppLayout title="Custo de Mercadoria">
      <div className="flex flex-col gap-5 w-full">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-ink">Custo de Mercadoria</h1>
            <p className="text-sm text-ink-faint">
              Comparativo mensal · Período: <span className="text-ink-muted">{periodRangeLabel || '—'}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSyncNotas}
              disabled={syncing}
              className="px-4 py-2 bg-sunken hover:bg-line disabled:opacity-50 text-ink text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2"
              title="Puxa as notas de entrada do Tiny e recalcula as sugestões automáticas"
            >
              {syncing && <span className="w-3.5 h-3.5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />}
              {syncing ? 'Sincronizando…' : '↻ Sincronizar notas do Tiny'}
            </button>
            <button
              onClick={handleExport}
              disabled={entries.length === 0}
              className="px-4 py-2 bg-sunken hover:bg-line disabled:opacity-40 text-ink text-sm font-medium rounded-lg transition-colors"
            >
              Exportar Excel
            </button>
            <button
              onClick={handleAddNext}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              + Próximo mês
            </button>
          </div>
        </div>

        {error && <Alert type="error" message={error} />}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* KPI Cards — custo do mês corrente + contas a pagar */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {currentEntry && currentComputed && (
                <>
                  <KpiCard
                    label={`Custo real · ${monthLabel(currentEntry.period_year, currentEntry.period_month)}`}
                    value={brl(currentComputed.costReal)}
                  />
                  <KpiCard
                    label="Faturamento"
                    value={brl(currentEntry.revenue_gross)}
                    tone="info"
                  />
                  <KpiCard
                    label="% Custo"
                    value={currentComputed.costPct != null ? `${currentComputed.costPct.toFixed(1)}%` : '—'}
                    tone={currentComputed.status === 'ok' ? 'success'
                         : currentComputed.status === 'warning' ? 'warning'
                         : currentComputed.status === 'danger' ? 'danger'
                         : 'default'}
                    hint={`Meta: ${currentEntry.target_pct}%`}
                  />
                  <KpiCard
                    label="Meta em R$"
                    value={brl(currentComputed.targetValue)}
                    hint={`${currentEntry.target_pct}% do faturamento`}
                  />
                </>
              )}
              <KpiCard
                label="A pagar · em aberto"
                value={brl(payTotals.aberto)}
                tone="info"
                hint={
                  boletos === 'loading' ? 'carregando…'
                  : payTotals.proximo
                    ? `Próx. venc.: ${fmtDate(payTotals.proximo)}${payTotals.vencido > 0 ? ` · vencidos ${brl(payTotals.vencido)}` : ''}`
                    : 'sem boletos — sincronize as notas'
                }
              />
            </div>

            {/* Gráficos: previsão de contas a pagar + % custo (50/50) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ContasPagarForecastChart boletos={boletosArr} />
              <CostOfGoodsChart entries={entries} />
            </div>

            {/* Tabela de custos (esq.) + Pagamentos (dir.) — lado a lado em telas largas */}
            <div className="flex flex-col 2xl:flex-row gap-4 items-start">
            {/* Tabela editável */}
            <div className="bg-surface border border-line rounded-xl overflow-hidden w-full 2xl:flex-1 min-w-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-sunken border-b border-line">
                    <tr>
                      <th className="text-left text-xs font-semibold text-ink-muted uppercase px-3 py-2.5">Período</th>
                      <th className="text-right text-xs font-semibold text-sky-600 uppercase px-3 py-2.5" title="Editável">Custo Matriz ✎</th>
                      <th className="text-right text-xs font-semibold text-sky-600 uppercase px-3 py-2.5" title="Editável">Custo Filial ✎</th>
                      <th className="text-right text-xs font-semibold text-sky-600 uppercase px-3 py-2.5" title="Editável">Retorno Filial ✎</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2.5" title="Calculado: Matriz − Filial + Retorno">Custo Real</th>
                      <th className="text-right text-xs font-semibold text-sky-600 uppercase px-3 py-2.5" title="Editável">Faturamento ✎</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2.5" title="Calculado: Custo Real / Faturamento × 100">% Custo</th>
                      <th className="text-right text-xs font-semibold text-sky-600 uppercase px-3 py-2.5" title="Editável (default 55%)">Meta % ✎</th>
                      <th className="text-center text-xs font-semibold text-ink-muted uppercase px-3 py-2.5">Status</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2.5">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(e => {
                      const d = getDraft(e)
                      // Computa a partir do DRAFT (não da entry salva), pra refletir edições em tempo real
                      const previewEntry: CostOfGoodsEntry = {
                        ...e,
                        cost_matriz: d.cost_matriz,
                        cost_filial: d.cost_filial,
                        cost_filial_return: d.cost_filial_return,
                        revenue_gross: d.revenue_gross,
                        target_pct: d.target_pct,
                      }
                      const c = computeRow(previewEntry)
                      const dirty = isDirty(e)
                      const badge = STATUS_BADGE[c.status]
                      return (
                        <Fragment key={e.id}>
                        <tr className="border-b border-line hover:bg-sunken">
                          <td className="px-3 py-2 text-ink-muted font-mono text-xs whitespace-nowrap">
                            <button
                              onClick={() => toggleExpand(e)}
                              className="inline-flex items-center gap-1 hover:text-ink"
                              title="Ver notas de entrada e parcelas deste mês"
                            >
                              <span className={`transition-transform ${expandedId === e.id ? 'rotate-90' : ''}`}>▸</span>
                              {monthLabel(e.period_year, e.period_month)}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number" step="0.01"
                              value={d.cost_matriz}
                              onChange={ev => setField(e, 'cost_matriz', ev.target.value)}
                              className="w-28 bg-paper border border-line rounded px-2 py-1 text-right text-ink text-sm tabular-nums focus:border-sky-600 outline-none"
                            />
                            <AutoHint auto={e.cost_matriz_auto} current={d.cost_matriz} onUse={v => applyAuto(e, 'cost_matriz', v)} />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number" step="0.01"
                              value={d.cost_filial}
                              onChange={ev => setField(e, 'cost_filial', ev.target.value)}
                              className="w-28 bg-paper border border-line rounded px-2 py-1 text-right text-ink text-sm tabular-nums focus:border-sky-600 outline-none"
                            />
                            <AutoHint auto={e.cost_filial_auto} current={d.cost_filial} onUse={v => applyAuto(e, 'cost_filial', v)} />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number" step="0.01"
                              value={d.cost_filial_return}
                              onChange={ev => setField(e, 'cost_filial_return', ev.target.value)}
                              className="w-28 bg-paper border border-line rounded px-2 py-1 text-right text-ink text-sm tabular-nums focus:border-sky-600 outline-none"
                            />
                            <AutoHint auto={e.cost_filial_return_auto} current={d.cost_filial_return} onUse={v => applyAuto(e, 'cost_filial_return', v)} />
                          </td>
                          <td className="px-3 py-2 text-right text-ink-muted tabular-nums whitespace-nowrap" title="Matriz − Filial + Retorno">
                            {brl(c.costReal)}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number" step="0.01"
                              value={d.revenue_gross}
                              onChange={ev => setField(e, 'revenue_gross', ev.target.value)}
                              className="w-28 bg-paper border border-line rounded px-2 py-1 text-right text-ink text-sm tabular-nums focus:border-sky-600 outline-none"
                            />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium whitespace-nowrap">
                            <span className={
                              c.status === 'ok' ? 'text-emerald-600'
                              : c.status === 'warning' ? 'text-warn'
                              : c.status === 'danger' ? 'text-danger'
                              : 'text-ink-faint'
                            }>
                              {c.costPct != null ? `${c.costPct.toFixed(1)}%` : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number" step="0.01" min="0" max="100"
                              value={d.target_pct}
                              onChange={ev => setField(e, 'target_pct', ev.target.value)}
                              className="w-16 bg-paper border border-line rounded px-2 py-1 text-right text-ink text-sm tabular-nums focus:border-sky-600 outline-none"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${badge.cls}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              onClick={() => handleSave(e)}
                              disabled={!dirty || savingId === e.id}
                              className="text-xs px-2.5 py-1 bg-sky-700 hover:bg-sky-600 disabled:opacity-30 disabled:cursor-not-allowed rounded text-white mr-1.5"
                            >
                              {savingId === e.id ? '...' : 'Salvar'}
                            </button>
                            <button
                              onClick={() => handleDelete(e)}
                              disabled={deletingId === e.id}
                              className="text-xs px-2.5 py-1 bg-red-900/40 hover:bg-red-900/60 border border-red-700/50 disabled:opacity-30 rounded text-red-200"
                              title="Excluir período"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                        {expandedId === e.id && (
                          <tr className="bg-sunken/50 border-b border-line">
                            <td colSpan={10} className="px-4 py-3">
                              <InvoicesPanel data={invoicesByEntry[e.id]} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      )
                    })}
                    {entries.length === 0 && (
                      <tr><td colSpan={10} className="px-4 py-8 text-center text-ink-faint">
                        Nenhum período cadastrado. Clique em "+ Próximo mês" para começar.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagamentos das notas de entrada — coluna da direita (empresa ativa) */}
            <div className="w-full 2xl:w-[500px] 2xl:shrink-0">
              <PagamentosSection data={boletos} open={boletosOpen} onToggle={toggleBoletos} />
            </div>
            </div>

            <p className="text-xs text-ink-faint">
              💡 Células com <span className="text-sky-600">✎</span> são editáveis. As demais (Custo Real, % Custo) são calculadas em tempo real. Clique em <span className="text-sky-600">Salvar</span> para persistir as alterações.
            </p>
          </>
        )}
      </div>
    </AppLayout>
  )
}
